// Sprint 1 — Onboarding post-registro
import { motion } from "motion/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";

type Props = { slug: string };

export function OnboardingStepSuccess({ slug }: Props) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const publicUrl = `${origin}/book/${slug}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className={css({ textAlign: "center", maxW: "28rem", mx: "auto", py: "6" })}>
      <div className={css({ display: "flex", justifyContent: "center", mb: "8" })}>
        <svg width="88" height="88" viewBox="0 0 88 88" aria-hidden>
          <motion.circle
            cx="44"
            cy="44"
            r="40"
            fill="none"
            stroke="#6366f1"
            strokeWidth="3"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            style={{ transformOrigin: "44px 44px" }}
          />
          <motion.path
            d="M28 46 L38 56 L60 34"
            fill="none"
            stroke="#4f46e5"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.45, ease: "easeOut" }}
          />
        </svg>
      </div>

      <h1
        className={css({
          fontSize: "2xl",
          fontWeight: "bold",
          color: "token(colors.brand.600)",
          mb: "3",
        })}
      >
        ¡Tu agenda está lista!
      </h1>
      <p className={css({ fontSize: "sm", color: "#64748b", mb: "8", lineHeight: 1.6 })}>
        Tu agenda fue creada con éxito y ya está lista para recibir reservas.
      </p>

      <div
        className={cx(
          cf.dashSurface,
          css({ p: "4", mb: "4", textAlign: "left", cursor: "pointer" }),
        )}
        onClick={() => void copy()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            void copy();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <p className={css({ fontSize: "xs", color: "#64748b", mb: "1" })}>Link público</p>
        <p className={css({ fontSize: "sm", color: "#4f46e5", wordBreak: "break-all" })}>{publicUrl}</p>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            void copy();
          }}
          className={css({
            mt: "3",
            fontSize: "xs",
            fontWeight: "semibold",
            color: "token(colors.brand.700)",
            bg: "rgba(99, 102, 241, 0.12)",
            border: "none",
            rounded: "md",
            px: "3",
            py: "1.5",
            cursor: "pointer",
          })}
        >
          {copied ? "Copiado" : "Copiar"}
        </button>
      </div>

      <div
        className={css({
          display: "flex",
          flexDir: { base: "column", sm: "row" },
          gap: "3",
          justifyContent: "center",
        })}
      >
        <a
          href={publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cx(
            cf.linkPrimaryOnDark,
            css({ textAlign: "center", px: "5", py: "3", fontSize: "sm" }),
          )}
        >
          Ver mi agenda →
        </a>
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className={cx(
            cf.linkOutlineOnDark,
            css({
              px: "5",
              py: "3",
              fontSize: "sm",
              border: "2px solid #e2e8f0",
              color: "#0f172a",
              bg: "white",
              cursor: "pointer",
              rounded: "lg",
            }),
          )}
        >
          Ir al panel
        </button>
      </div>
    </div>
  );
}
